import moment from 'moment';
import AppService from 'App/legacy/AppService';
import { previewText } from 'Helpers/markdown';
import type PublicChatbotConversation from 'App/resources/Model/PublicChatbotConversation';
import type {
	PublicChatbotMessage,
	PublicChatbotMessageAttachment,
	PublicChatbotMessageLiveSource,
	PublicChatbotMessageSource
} from 'App/resources/Model/PublicChatbotConversation';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * What the chat was handed over as (WEB-13681). A lead hand-over stamps `todo`
 * for a to-do and a phone call alike; the activity's type says which.
 */
export const handOverKind = (
	conversation: Pick<PublicChatbotConversation, 'escalatedEntity' | 'escalatedActivityTypeId'>
): 'ticket' | 'todo' | 'phonecall' | null => {
	const entity = conversation.escalatedEntity;
	if (entity !== 'todo') return entity ?? null;
	return conversation.escalatedActivityTypeId === AppService.getTodoTypes().PHONE_CALL.id ? 'phonecall' : 'todo';
};

/**
 * The chat was handed over, but its to-do, phone call or ticket has been
 * deleted (WEB-13681). The stamp on the chat outlives the record; the fields
 * read off the record do not, and these two come from columns that are never
 * NULL while it exists (`client_activity.activitytype_id`, `ticket.title`).
 * ui-3 reads it the same way (lib/format/websiteChat.ts `websiteChatRecordDeleted`).
 */
export const handOverDeleted = (
	conversation: Pick<PublicChatbotConversation, 'escalatedEntity' | 'escalatedActivityTypeId' | 'escalatedTitle'>
): boolean => {
	if (conversation.escalatedEntity === 'todo') return conversation.escalatedActivityTypeId == null;
	if (conversation.escalatedEntity === 'ticket') return conversation.escalatedTitle == null;
	return false;
};

/**
 * Who the conversation is with, as every pane labels it: the name, else the
 * email the sign-in carried, else "Erased person" once GDPR erasure ran, else
 * the CRM contact a hand-over reached, else "Anonymous visitor" for someone
 * who never signed in (ui-3 shares the wording).
 */
export const personLabel = (conversation: PublicChatbotConversation, t: Translate): string => {
	const person = conversation.person;
	if (person?.name || person?.email) return (person.name || person.email) as string;
	if (person?.erasedAt) return t('publicChatbot.person.erased');
	// A hand-over keeps the email on the CRM contact it created, not on the person (WEB-12989).
	if (person?.contact?.name) return person.contact.name;
	return t('publicChatbot.websiteChats.anonymousVisitor');
};

/** The visitor's name when they gave one or a hand-over found one; null for an email-only or anonymous visitor. */
export const visitorName = (conversation: PublicChatbotConversation): string | null =>
	conversation.person?.name || conversation.person?.contact?.name || null;

/**
 * The record view's title (WEB-13681): the bot's own summary of the hand-over -
 * its `topic`, the reason it gave when handing over (a ticket is titled with
 * it; a to-do's description only names the chat), else the question it missed,
 * else what the visitor opened with.
 */
export const chatTitle = (conversation: PublicChatbotConversation): string | null =>
	conversation.topic?.trim() || conversation.firstVisitorMessage?.trim() || null;

/** When the chat last moved: its latest turn, else when it started. */
export const lastActivity = (conversation: PublicChatbotConversation): string =>
	conversation.lastMessageDate ?? conversation.regDate;

/**
 * A list row's last message (WEB-13681): the latest turn and who wrote it — the
 * bot, or the visitor by first name. A tool or event row carries no speaker,
 * and a chat with no turn falls back to what it was about.
 */
export const lastLine = (
	conversation: PublicChatbotConversation,
	t: Translate
): { speaker: string | null; text: string | null } => {
	const role = conversation.lastMessageRole;
	const speaker =
		role === 'assistant'
			? t('publicChatbot.websiteChats.bot')
			: role === 'user'
			? visitorName(conversation)?.split(' ')[0] ?? t('publicChatbot.websiteChats.visitor')
			: null;
	return {
		speaker,
		// The bot writes markdown, flattened for the one-line preview; what the visitor typed stays as typed.
		text:
			role === 'assistant' && conversation.lastMessage
				? previewText(conversation.lastMessage)
				: conversation.lastMessage ?? conversation.topic ?? conversation.firstVisitorMessage ?? null
	};
};

const COMPACT_UNITS = ['minute', 'hour', 'day'] as const;

/**
 * How long ago, the short way (WEB-13681): "Now" under a minute, then "7m",
 * "3h", "2d" for under a week, else the date. The unit letters follow the UI
 * language (moment's locale), the date the user's region format.
 */
export const compactRelativeTime = (date: string, t: Translate): string => {
	const at = moment(date);
	if (!at.isValid()) return '';
	const [minutes, hours, days] = COMPACT_UNITS.map(
		unit => new Intl.NumberFormat(moment.locale(), { style: 'unit', unit, unitDisplay: 'narrow' })
	);
	const minute = Math.round(moment().diff(at, 'seconds') / 60);
	if (minute < 1) return t('default.now');
	if (minute < 60) return minutes.format(minute);
	const hour = Math.round(minute / 60);
	if (hour < 24) return hours.format(hour);
	const day = Math.round(hour / 24);
	return day < 7 ? days.format(day) : at.format('L');
};

/** The text erasure writes over every turn (base `ANONYMIZED_TEXT`). */
const ERASED_TEXT = 'Anonymized';

/** A turn whose text GDPR erasure replaced — only on a person who was erased, so a typed "Anonymized" stays. */
export const isErasedMessage = (conversation: PublicChatbotConversation, message: PublicChatbotMessage) =>
	!!conversation.person?.erasedAt && message.content === ERASED_TEXT;

/** One name in a bot turn's "Answered from" line: a library item or a document from a knowledge connection. */
export type AnsweredFrom = {
	key: string;
	name: string;
	/** Only an absolute http(s) link — a connection's recipe may record a bare id as its url. */
	url: string | null;
	/** A library item, which an admin opens in the knowledge library when it has no page of its own. */
	library: boolean;
};

/** A link the line may open: absolute http(s) only, so a relative id or a `javascript:` value never becomes an href. */
const webUrl = (url: string | null | undefined): string | null => (url && /^https?:\/\//i.test(url) ? url : null);

/**
 * The knowledge a bot turn answered from: one entry per library item (an
 * answer usually draws several chunks of the same page), then one per live
 * document from a knowledge connection (`metadata.liveSources`). Empty for a
 * person's turn, a turn with no retrieval, or a thread that was erased. ui-3:
 * lib/format/websiteChat.ts `websiteChatSources`.
 */
export const messageSources = (message: PublicChatbotMessage): AnsweredFrom[] => {
	const library = message.metadata?.sources;
	const live = message.metadata?.liveSources;
	const all: AnsweredFrom[] = [
		...(Array.isArray(library) ? (library as PublicChatbotMessageSource[]) : []).map(source => ({
			key: `item:${source?.itemId}`,
			name: source?.name ?? '',
			url: webUrl(source?.url),
			library: true
		})),
		...(Array.isArray(live) ? (live as PublicChatbotMessageLiveSource[]) : []).map(source => ({
			key: `live:${source?.connectionId}:${source?.id}`,
			name: source?.title || source?.id || '',
			url: webUrl(source?.url),
			library: false
		}))
	];
	const seen = new Set<string>();
	return all.filter(source => {
		if (!source.name || seen.has(source.key)) return false;
		seen.add(source.key);
		return true;
	});
};

/**
 * The files a person attached to a turn (WEB-12351), each with its position —
 * the download endpoint addresses a file by message and index. Empty for a bot
 * turn, a turn with no file, and an erased one (erasure removes the record).
 */
export const messageAttachments = (
	message: PublicChatbotMessage
): Array<PublicChatbotMessageAttachment & { index: number; isImage: boolean }> => {
	const raw = message.metadata?.attachments;
	if (!Array.isArray(raw)) return [];
	return (raw as PublicChatbotMessageAttachment[])
		.map((file, index) => ({ ...file, index, isImage: /^image\//.test(file?.mimeType ?? '') }))
		.filter(file => !!file.name);
};

export type ChatFile = PublicChatbotMessageAttachment & { messageId: number; index: number; regDate: string | null };

/** The files the visitor sent, in the order they were sent (WEB-13681) - the record view's Files tab. */
export const chatFiles = (messages: PublicChatbotMessage[]): ChatFile[] =>
	messages.flatMap(message =>
		message.role === 'user'
			? messageAttachments(message).map(file => ({
					name: file.name,
					mimeType: file.mimeType,
					sizeBytes: file.sizeBytes,
					messageId: message.id,
					index: file.index,
					regDate: message.regDate
			  }))
			: []
	);

/** Past this pause a new turn from the same sender starts a new run. */
export const RUN_GAP_MINUTES = 5;

/**
 * Whether `next` joins the run `prev` is in (WEB-12369) — the run the thread
 * draws as one turn: the sender's name once on top, the bubbles stacked, the
 * avatar beside the last one. Same sender, and no more
 * than five minutes after the turn before; a turn without a time cannot
 * measure a gap, so the sender alone decides. The website widget draws its
 * transcript by the same rule (public-chatbot-widget/src/runs.ts), so the rep
 * and the visitor see the same turns — keep the two in step.
 */
export const continuesRun = (prev: PublicChatbotMessage | undefined, next: PublicChatbotMessage): boolean => {
	if (!prev || prev.role !== next.role) return false;
	if (!prev.regDate || !next.regDate) return true;
	return moment(next.regDate).diff(moment(prev.regDate), 'seconds') <= RUN_GAP_MINUTES * 60;
};

/**
 * For each turn, whether it opens and whether it closes its run. `breakBefore`
 * forces a new run where the thread draws something between two turns (a day
 * chip, the escalation chip, the erased-turns line).
 */
export const markRuns = (
	messages: PublicChatbotMessage[],
	breakBefore: (index: number) => boolean = () => false
): Array<{ first: boolean; last: boolean }> => {
	const joins = messages.map((message, index) => !breakBefore(index) && continuesRun(messages[index - 1], message));
	return messages.map((_, index) => ({ first: !joins[index], last: !joins[index + 1] }));
};

type TraceEntry = { name?: string; output?: unknown };

/** A trace field as JSON, or null — a trace field is capped at 2000 characters, so a cut-off one is not JSON. */
const parseTraceField = (value: unknown): Record<string, unknown> | null => {
	if (typeof value !== 'string') return null;
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

/** Whether a turn ran `handOverToHuman` and it succeeded, read from its `metadata.toolTrace`. */
const handedOver = (message: PublicChatbotMessage): boolean => {
	const trace = message.metadata?.toolTrace;
	if (!Array.isArray(trace)) return false;
	return (trace as TraceEntry[]).some(
		entry => entry?.name === 'handOverToHuman' && parseTraceField(entry.output)?.ok === true
	);
};

/**
 * When the chat ran, for a card's header: its first turn's date and time, then
 * up to its last — "2026-10-07 15:04–15:12" within a day, both dates across
 * days, one moment when the two match. Empty when no turn carries a time.
 */
export const chatTimeSpan = (messages: PublicChatbotMessage[]): string => {
	const times = messages.filter(message => message.regDate).map(message => moment(message.regDate));
	if (!times.length) return '';
	const start = times[0];
	const end = times[times.length - 1];
	const from = start.format('L LT');
	if (end.format('L LT') === from) return from;
	return end.isSame(start, 'day') ? `${from}–${end.format('LT')}` : `${from} – ${end.format('L LT')}`;
};

/**
 * Where the thread draws the escalation chip: just before the bot turn that
 * confirmed the hand-over, read from its `metadata.toolTrace`. The newest such
 * turn wins — a person can be handed over again once the first to-do closes,
 * and the chip links the newest record. Threads with no trace (written before
 * the trace recorded it, or erased — erasure strips it) keep the old placement
 * rule: before the bot's last turn, or closing the thread when the bot never
 * spoke. That guess is wrong when the person kept chatting after the hand-over.
 */
export const escalationIndex = (messages: PublicChatbotMessage[]): number => {
	for (let i = messages.length - 1; i >= 0; i--) {
		if (messages[i].role !== 'user' && handedOver(messages[i])) return i;
	}
	for (let i = messages.length - 1; i >= 0; i--) {
		if (messages[i].role !== 'user') return i;
	}
	return messages.length;
};

/** What the chat gave the company's marketing score, signed: "+40". */
export const leadScoreLabel = (score: number): string => `${score < 0 ? '' : '+'}${score}`;
